import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useRef } from 'react';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { videoService } from '@/services/videoService';
import { useHierarchicalFoldersStore } from '@/store/hierarchicalFoldersStore';

export function useDuplicateVideo() {
  useLanguage();
  const client = useQueryClient();
  const mutationKey = ['duplicate-video'];
  const pendingCount = useIsMutating({ mutationKey });
  const submitting = useRef(false);
  const mutation = useMutation({
    mutationKey,
    retry: false,
    mutationFn: videoService.duplicateVideo,
    onSuccess: async () => {
      const folders = useHierarchicalFoldersStore.getState();
      await Promise.allSettled([
        client.invalidateQueries({ queryKey: ['videos', 'list'] }),
        client.invalidateQueries({ queryKey: ['trial-quota', 'videos-count'] }),
        folders.fetchRootFolders(),
        ...(folders.currentFolder ? [folders.fetchFolderDetails(folders.currentFolder.id)] : []),
      ]);
      toast.success(translateInterfaceText("Vídeo duplicado com configurações padrão."));
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || translateInterfaceText("Não foi possível duplicar o vídeo. Tente novamente."));
    },
    onSettled: () => { submitting.current = false; },
  });

  return {
    isPending: pendingCount > 0,
    duplicateVideo: (id: string) => {
      if (submitting.current || client.isMutating({ mutationKey }) > 0) return;
      submitting.current = true;
      mutation.mutate(id);
    },
  };
}
